import { useState } from 'react';
import '../styles/App.css';
import '../styles/ListMyBusiness.css';
import Navbar from '../components/NavBar';
import Footer from '../components/Footer';
import { useTranslation } from "react-i18next";
import { isValidEmail } from "../utils/functions";

const catPhotoPath = '/Images/ListMyBusinessPage.jpeg';

function DiscoverMorePage() {
    const [t] = useTranslation();
    const [errorMessage, setErrorMessage] = useState("");
    const [phoneCode, setPhoneCode] = useState("+852");
    const [termsAccepted, setTermsAccepted] = useState(false);

    const [formData, setFormData] = useState({
        contactPerson: '',
        phoneNumber: '',
        companyNameEN: '',
        companyNameZH: '',
        email: '',
        website: '',
        facebook: '',
        instagram: '',
    });

    const [errors, setErrors] = useState({});

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        if (value.trim() !== '') {
            setErrors(prev => ({ ...prev, [name]: false }));
        }
    };

    const handlePhoneCodeChange = (e) => {
        setPhoneCode(e.target.value);
    };

    const handleTermsChange = (e) => {
        setTermsAccepted(e.target.checked);
        setErrors(prev => ({ ...prev, termsAccepted: false }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const newErrors = {
            contactPerson: !formData.contactPerson.trim(),
            phoneNumber: !formData.phoneNumber.trim(),
            companyNameEN: !formData.companyNameEN.trim(),
            email: !formData.email.trim(),
            website: !formData.website.trim(),
            termsAccepted: !termsAccepted,
        };

        setErrors(newErrors);
        const hasErrors = Object.values(newErrors).some(Boolean);
        if (hasErrors) {
            setErrorMessage(t("list_my_business.required_fields_error"));
            return;
        }

        if (!isValidEmail(formData.email)) {
            setErrorMessage(t("list_my_business.email_error"));
            return;
        }

        if (isNaN(formData.phoneNumber.replace(/\D/g, ''))) {
            setErrorMessage(t("list_my_business.number_error"));
            return;
        }

        const payload = new FormData();
        const data = {
            contactPerson: formData.contactPerson,
            phoneNumber: `${phoneCode}${formData.phoneNumber.replace(/\D/g, '')}`,
            companyNameEN: formData.companyNameEN,
            companyNameZH: '',
            email: formData.email,
            website: formData.website,
            facebook: formData.facebook,
            instagram: formData.instagram,
            descriptionEN: 'N/A',
            descriptionZH: 'N/A',
        };

        payload.append('data', JSON.stringify(data));

        try {
            const response = await fetch('https://backend.activikid.hk/api/businesses', {
                method: 'POST',
                body: payload,
            });

            const responseData = await response.json();

            if (!response.ok) {
                console.error('Backend error:', JSON.stringify(responseData, null, 2));
                const errDetails = responseData.error?.details?.errors
                    ?.map(e => e.message).join(', ');
                setErrorMessage(
                    errDetails ||
                    responseData.error?.message ||
                    responseData.message ||
                    "Submission failed. Please try again."
                );
                return;
            }

            setErrorMessage("");
            alert("Form submitted successfully!");

            setFormData({
                contactPerson: '',
                phoneNumber: '',
                companyNameEN: '',
                companyNameZH: '',
                email: '',
                website: '',
                facebook: '',
                instagram: '',
            });
            setTermsAccepted(false);

        } catch (error) {
            console.error('Network error:', error);
            setErrorMessage("Network error - could not connect to server");
        }
    };

    return (
        <>
            <Navbar />
            <div className="form-outer-container" style={{ marginTop: '2rem' }}>
                <div className="container form-container">
                    <div className="header-image-wrapper" style={{ paddingTop: '0px', marginBottom: '45px' }}>
                        <img
                            src={catPhotoPath}
                            alt="Discover More on ActiviKid"
                            className="header-image"
                        />
                    </div>

                    <h2 className="form-title text-center" style={{ fontSize: '2.4rem' }}>{t('list_my_business.title')}</h2>
                    <p className="form-subtitle text-center">{t('list_my_business.subtitle')}</p>

                    <form onSubmit={handleSubmit} noValidate>
                        <div className="row">
                            <div className="col-md-6">
                                <label>{t('list_my_business.contact_person')} <span className="required-star">*</span></label>
                                <input
                                    className={`form-input ${errors.contactPerson ? 'input-error' : ''}`}
                                    name="contactPerson"
                                    value={formData.contactPerson}
                                    onChange={handleChange}
                                    placeholder="e.g. Jason Chan"
                                    required
                                />
                            </div>
                            <div className="col-md-6">
                                <label>{t('list_my_business.phone_number')} <span className="required-star">*</span></label>
                                <div className={`phone-input-group ${errors.phoneNumber ? 'input-error' : ''}`}>
                                    <select
                                        className="phone-code-select"
                                        value={phoneCode}
                                        onChange={handlePhoneCodeChange}
                                    >
                                        <option value="+852">+852</option>
                                        <option value="+853">+853</option>
                                        <option value="+86">+86</option>
                                    </select>
                                    <span className="phone-divider">|</span>
                                    <input
                                        className="phone-number-input"
                                        name="phoneNumber"
                                        value={formData.phoneNumber}
                                        onChange={handleChange}
                                        placeholder="e.g. 9123 4567"
                                        required
                                    />
                                </div>
                            </div>

                            <div className="col-md-6">
                                <label>Company Name <span className="required-star">*</span></label>
                                <input
                                    className={`form-input ${errors.companyNameEN ? 'input-error' : ''}`}
                                    name="companyNameEN"
                                    value={formData.companyNameEN}
                                    onChange={handleChange}
                                    placeholder="ABC Space"
                                    required
                                />
                            </div>

                            <div className="col-md-6">
                                <label>{t('list_my_business.email')} <span className="required-star">*</span></label>
                                <input
                                    className={`form-input ${errors.email ? 'input-error' : ''}`}
                                    name="email"
                                    type="email"
                                    value={formData.email}
                                    onChange={handleChange}
                                    placeholder="e.g. cooperatewithus@gmail.com"
                                    required
                                />
                            </div>

                            <div className="col-md-6">
                                <label>{t('list_my_business.website')} <span className="required-star">*</span></label>
                                <input
                                    className={`form-input ${errors.website ? 'input-error' : ''}`}
                                    name="website"
                                    value={formData.website}
                                    onChange={handleChange}
                                    placeholder="e.g. www.abcspace.hk"
                                    required
                                />
                            </div>

                            <div className="col-md-6">
                                <div className="row">
                                    <div className="col-md-6">
                                        <label>{t('list_my_business.facebook')}</label>
                                        <input
                                            className="form-input"
                                            name="facebook"
                                            value={formData.facebook}
                                            onChange={handleChange}
                                            placeholder="e.g. fb.com/ABC"
                                        />
                                    </div>
                                    <div className="col-md-6">
                                        <label>{t('list_my_business.instagram')}</label>
                                        <input
                                            className="form-input"
                                            name="instagram"
                                            value={formData.instagram}
                                            onChange={handleChange}
                                            placeholder="e.g. instagram.com/ABC"
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="col-12">
                                <div className={`terms-container ${errors.termsAccepted ? 'terms-error' : ''}`}>
                                    <label className="terms-label">
                                        <input
                                            type="checkbox"
                                            className="terms-checkbox"
                                            checked={termsAccepted}
                                            onChange={handleTermsChange}
                                            required
                                        />
                                        <span className="terms-custom-checkbox"></span>
                                        <span className="terms-text">
                                            {t('list_my_business.terms_text')}
                                        </span>
                                    </label>
                                </div>
                            </div>

                            {errorMessage && (
                                <div className="error-message">{errorMessage}</div>
                            )}

                            <div className="col-12 text-center">
                                <button type="submit" className="submit-button">
                                    {t('list_my_business.submit')}
                                </button>
                            </div>
                        </div>
                    </form>
                </div>
                <Footer />
            </div>
        </>
    );
}

export default DiscoverMorePage;
